import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Site (público)
import ptCommon from './locales/pt-BR/common.json';
import ptHome from './locales/pt-BR/site.home.json';
import ptPlataforma from './locales/pt-BR/site.plataforma.json';
import ptCases from './locales/pt-BR/site.cases.json';
import ptCiencia from './locales/pt-BR/site.ciencia.json';
import ptEcossistema from './locales/pt-BR/site.ecossistema.json';
import ptFuturo from './locales/pt-BR/site.futuro.json';
import ptContato from './locales/pt-BR/site.contato.json';
import ptAuth from './locales/pt-BR/auth.json';
import ptApp from './locales/pt-BR/app.json';
import ptAppComum from './locales/pt-BR/app_comum.json';
import ptAppClinico from './locales/pt-BR/app_clinico.json';
import ptAppAdmin from './locales/pt-BR/app_admin.json';
import ptAppCirurgia from './locales/pt-BR/app_cirurgia.json';

import enCommon from './locales/en-US/common.json';
import enHome from './locales/en-US/site.home.json';
import enPlataforma from './locales/en-US/site.plataforma.json';
import enCases from './locales/en-US/site.cases.json';
import enCiencia from './locales/en-US/site.ciencia.json';
import enEcossistema from './locales/en-US/site.ecossistema.json';
import enFuturo from './locales/en-US/site.futuro.json';
import enContato from './locales/en-US/site.contato.json';
import enAuth from './locales/en-US/auth.json';
import enApp from './locales/en-US/app.json';
import enAppComum from './locales/en-US/app_comum.json';
import enAppClinico from './locales/en-US/app_clinico.json';
import enAppAdmin from './locales/en-US/app_admin.json';
import enAppCirurgia from './locales/en-US/app_cirurgia.json';

import esCommon from './locales/es/common.json';
import esHome from './locales/es/site.home.json';
import esPlataforma from './locales/es/site.plataforma.json';
import esCases from './locales/es/site.cases.json';
import esCiencia from './locales/es/site.ciencia.json';
import esEcossistema from './locales/es/site.ecossistema.json';
import esFuturo from './locales/es/site.futuro.json';
import esContato from './locales/es/site.contato.json';
import esAuth from './locales/es/auth.json';
import esApp from './locales/es/app.json';
import esAppComum from './locales/es/app_comum.json';
import esAppClinico from './locales/es/app_clinico.json';
import esAppAdmin from './locales/es/app_admin.json';
import esAppCirurgia from './locales/es/app_cirurgia.json';

export const LOCALES = ['pt-BR', 'en-US', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

/** URL prefix for each locale ("" = default, sem prefixo). */
export const LOCALE_PREFIX: Record<Locale, string> = {
  'pt-BR': '',
  'en-US': '/en',
  es: '/es',
};

/** Reverse lookup: URL prefix → locale. */
export function localeFromPath(pathname: string): Locale {
  if (pathname.startsWith('/en/') || pathname === '/en') return 'en-US';
  if (pathname.startsWith('/es/') || pathname === '/es') return 'es';
  return 'pt-BR';
}

/** Strip prefix da URL para obter caminho lógico. */
export function stripLocalePrefix(pathname: string): string {
  if (pathname.startsWith('/en/')) return pathname.slice(3) || '/';
  if (pathname === '/en') return '/';
  if (pathname.startsWith('/es/')) return pathname.slice(3) || '/';
  if (pathname === '/es') return '/';
  return pathname;
}

/**
 * Rotas que NÃO existem em versão prefixada por idioma. O router registra o site 3× (`/`,
 * `/en`, `/es`) mas registra `/login`, `/recuperar-senha` e `/app/*` uma única vez, sem
 * prefixo. Navegar para `/en/app/medico` não casa com rota nenhuma e dá tela branca.
 */
const NON_LOCALIZED_PREFIXES = ['/app', '/login', '/recuperar-senha'];

/** Recebe o caminho JÁ sem prefixo de idioma (ver stripLocalePrefix). */
export function isLocalizedRoute(logicalPath: string): boolean {
  return !NON_LOCALIZED_PREFIXES.some(
    (p) => logicalPath === p || logicalPath.startsWith(`${p}/`),
  );
}

/** Constrói URL localizada a partir do caminho lógico. */
export function buildLocalizedPath(logicalPath: string, locale: Locale): string {
  const clean = logicalPath.startsWith('/') ? logicalPath : `/${logicalPath}`;
  const prefix = LOCALE_PREFIX[locale];
  // /login e /app/* não têm versão por idioma: o "Acessar Painel" do site em /en levava a /en/login (404).
  if (!prefix || !isLocalizedRoute(clean.split(/[?#]/)[0])) return clean;
  if (clean === '/') return prefix;
  return `${prefix}${clean}`;
}

export const NAMESPACES = [
  'common',
  'site_home',
  'site_plataforma',
  'site_cases',
  'site_ciencia',
  'site_ecossistema',
  'site_futuro',
  'site_contato',
  'auth',
  'app',
  'app_comum',
  'app_clinico',
  'app_admin',
  'app_cirurgia',
] as const;

export const resources = {
  'pt-BR': {
    common: ptCommon,
    site_home: ptHome,
    site_plataforma: ptPlataforma,
    site_cases: ptCases,
    site_ciencia: ptCiencia,
    site_ecossistema: ptEcossistema,
    site_futuro: ptFuturo,
    site_contato: ptContato,
    auth: ptAuth,
    app: ptApp,
    app_comum: ptAppComum,
    app_clinico: ptAppClinico,
    app_admin: ptAppAdmin,
    app_cirurgia: ptAppCirurgia,
  },
  'en-US': {
    common: enCommon,
    site_home: enHome,
    site_plataforma: enPlataforma,
    site_cases: enCases,
    site_ciencia: enCiencia,
    site_ecossistema: enEcossistema,
    site_futuro: enFuturo,
    site_contato: enContato,
    auth: enAuth,
    app: enApp,
    app_comum: enAppComum,
    app_clinico: enAppClinico,
    app_admin: enAppAdmin,
    app_cirurgia: enAppCirurgia,
  },
  es: {
    common: esCommon,
    site_home: esHome,
    site_plataforma: esPlataforma,
    site_cases: esCases,
    site_ciencia: esCiencia,
    site_ecossistema: esEcossistema,
    site_futuro: esFuturo,
    site_contato: esContato,
    auth: esAuth,
    app: esApp,
    app_comum: esAppComum,
    app_clinico: esAppClinico,
    app_admin: esAppAdmin,
    app_cirurgia: esAppCirurgia,
  },
} as const;

// Inicialização sync — todos os resources embutidos.
i18n.use(initReactI18next).init({
  resources,
  lng: 'pt-BR',
  fallbackLng: 'pt-BR',
  supportedLngs: LOCALES as unknown as string[],
  defaultNS: 'common',
  ns: NAMESPACES as unknown as string[],
  interpolation: { escapeValue: false },
  react: {
    // Não usar Suspense: SSG renderiza sync
    useSuspense: false,
    // Rebind ao trocar idioma (roda sync no client, mas evita esperar timers)
    bindI18n: 'languageChanged',
  },
  returnEmptyString: false,
});

// <html lang> acompanha o idioma: leitores de tela e o tradutor do navegador dependem dele.
if (typeof document !== 'undefined') {
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
}

// Detecção de idioma feita SÓ no client, via cookie/navigator, aplicando
// via Router. Não usamos i18next-browser-languagedetector porque conflita
// com nossa lógica manual de prefixo de URL.

export default i18n;
